import { z } from 'zod'
import { issueStatusSchema } from './report-issue'

/**
 * The Derek tab's agreement surface: what the relevance judge said about an issue's articles next
 * to what the person did with them — the legacy Derek page's buckets, in Headline's vocabulary.
 *
 * Buckets are the legacy page's seven, plus `unplaced`: a Headline pull over-asks the data API, so
 * an article can be judged (kept, even) and still not land because the header's limit was reached.
 * Like `keptElsewhere`, `dedupRemoved` and `notJudged`, it is a placement fact, not a relevance
 * disagreement, so it sits outside the agreement maths.
 */
export const derekBucketSchema = z.enum([
  'bothKept',
  'bothRemoved',
  'derekRemovedKept',
  'removedDerekKeep',
  'dedupRemoved',
  'keptElsewhere',
  'unplaced',
  'notJudged',
])
export type DerekBucket = z.infer<typeof derekBucketSchema>

/** What the person did with the article, as the issue records it. */
export const derekActionSchema = z.enum(['kept', 'removed', 'keptElsewhere', 'notInIssue'])
export type DerekAction = z.infer<typeof derekActionSchema>

export const derekComparedItemSchema = z.object({
  hitId: z.string(),
  headline: z.string(),
  outlet: z.string().nullable(),
  url: z.string().nullable(),
  /** Null when the judge never saw this article (hand-added, or pulled before Derek ran). */
  verdict: z.enum(['keep', 'drop']).nullable(),
  reason: z.string().nullable(),
  action: derekActionSchema,
  bucket: derekBucketSchema,
})
export type DerekComparedItem = z.infer<typeof derekComparedItemSchema>

/** The legacy page's agreement maths: agreements over everything compared, placement facts excluded. */
export const derekAgreementSchema = z.object({
  agreeKeeps: z.number().int().nonnegative(),
  agreeDrops: z.number().int().nonnegative(),
  disagreements: z.number().int().nonnegative(),
  compared: z.number().int().nonnegative(),
  /** Null when nothing was compared — shown as a dash, never as 100%. */
  pct: z.number().int().min(0).max(100).nullable(),
})
export type DerekAgreement = z.infer<typeof derekAgreementSchema>

export const derekSectionAgreementSchema = z.object({
  /** The ISSUE section (`report_issue_sections.id`). */
  sectionId: z.string(),
  /** The live header behind it, when it still exists — what the tab's header list is keyed by. */
  liveSectionId: z.string().nullable(),
  name: z.string(),
  /** Whether the judge recorded anything for this header on the issue's pull. */
  judged: z.boolean(),
  agreement: derekAgreementSchema,
  counts: z.record(derekBucketSchema, z.number().int().nonnegative()),
  items: z.array(derekComparedItemSchema),
})
export type DerekSectionAgreement = z.infer<typeof derekSectionAgreementSchema>

/**
 * Where the compared issue comes from. A report the legacy portal still delivers has no Headline
 * issues to compare against, so the tab offers the portal's own sent reports as bases, read from
 * the same warehouse tables the legacy Derek page reads (`reports`, `report_hits`,
 * `report_candidates`, `llm_candidates`).
 */
export const derekIssueSourceSchema = z.enum(['headline', 'legacy'])
export type DerekIssueSource = z.infer<typeof derekIssueSourceSchema>

export const derekIssueAgreementSchema = z.object({
  /** A Headline issue id, or a legacy `reports.id` when `source` is `legacy`. */
  issueId: z.string(),
  issueDate: z.iso.date(),
  status: issueStatusSchema,
  reportPullId: z.string().nullable(),
  source: derekIssueSourceSchema,
  /** Whether the judge recorded anything for this issue's pull at all. */
  judged: z.boolean(),
  agreement: derekAgreementSchema,
  /** Every drop the judge proposed, whatever became of it. */
  derekDrops: z.number().int().nonnegative(),
  /** Every judged article a person removed. */
  removed: z.number().int().nonnegative(),
  sections: z.array(derekSectionAgreementSchema),
})
export type DerekIssueAgreement = z.infer<typeof derekIssueAgreementSchema>

/**
 * Run experiment: judge one header's articles in the chosen issue with the prompt as typed — the
 * draft box, the report's preamble and model as the tab shows them — and compare the verdicts with
 * what the person did, the way the saved agreement does. Nothing is saved; the legacy page's
 * "Run" did the same against a sent report.
 */
export const derekExperimentRequestSchema = z.object({
  /** The ISSUE section (`report_issue_sections.id`) whose articles are judged. */
  sectionId: z.uuid(),
  prompt: z.string().trim().min(1).max(8000),
  /** The report preamble as typed; null or absent = none. */
  preamble: z.string().max(4000).nullable().optional(),
  /** A judge-model slug; absent = the data API's default. */
  model: z.string().min(1).max(128).optional(),
})
export type DerekExperimentRequest = z.infer<typeof derekExperimentRequestSchema>

export const derekExperimentResultSchema = z.object({
  model: z.string(),
  articlesRequested: z.number().int().nonnegative(),
  articlesFound: z.number().int().nonnegative(),
  dropsProposed: z.number().int().nonnegative(),
  dedupDropsProposed: z.number().int().nonnegative().nullable(),
  latencyMs: z.number().int().nonnegative(),
  section: derekSectionAgreementSchema,
})
export type DerekExperimentResult = z.infer<typeof derekExperimentResultSchema>

/** A legacy sent report, as the tab's picker lists it beside the report's own issues. */
export const derekLegacyIssueSchema = z.object({
  /** The legacy `reports.id`. */
  id: z.string(),
  sentAt: z.iso.datetime(),
})
export type DerekLegacyIssue = z.infer<typeof derekLegacyIssueSchema>

export const derekLegacyIssueListSchema = z.object({
  issues: z.array(derekLegacyIssueSchema),
})
export type DerekLegacyIssueList = z.infer<typeof derekLegacyIssueListSchema>
